import React, { useEffect, useState, useCallback } from 'react';
import {
  Phone, MapPin, Link as LinkIcon, Headphones, ExternalLink, Loader2,
  Briefcase, Users, Calendar, BarChart3, Wrench, Globe,
} from 'lucide-react';
import { toast } from 'sonner';

const ICONS = {
  phone: Phone, 'map-pin': MapPin, link: LinkIcon, headset: Headphones,
  briefcase: Briefcase, users: Users, calendar: Calendar, chart: BarChart3,
  wrench: Wrench, globe: Globe,
};

export function iconFor(name) {
  return ICONS[name] || LinkIcon;
}

// Reusable launcher shown to any authorized panel user (admins + employees).
// Fetches only the links THIS user may access; each click is recorded, then the
// configured URL opens in a new tab.
export default function StaffToolsLauncher({ api, compact = false }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('GET', '/staff-links');
      setLinks(res.data.links || []);
    } catch (e) {
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const open = async (link) => {
    setOpening(link.id);
    // Open the tab synchronously so the browser doesn't block the popup,
    // then point it at the configured URL once the click is recorded.
    const win = window.open('', '_blank');
    try {
      const res = await api('POST', `/staff-links/${link.id}/click`);
      const url = res.data?.url || link.url;
      if (win) win.location.href = url;
      else window.open(url, '_blank');
    } catch (e) {
      if (win) win.close();
      toast.error(e.response?.data?.detail || 'Could not open this tool');
    } finally {
      setOpening(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Loading tools…</div>;
  }
  if (links.length === 0) {
    return (
      <div className="text-center py-10 text-slate-400" data-testid="staff-tools-empty">
        <Wrench className="mx-auto mb-2 text-slate-300" size={32} />
        <p className="text-sm">No staff tools assigned to you yet.</p>
      </div>
    );
  }

  return (
    <div className={`grid gap-3 ${compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`} data-testid="staff-tools-launcher">
      {links.map((link) => {
        const Icon = iconFor(link.icon);
        return (
          <button
            key={link.id}
            onClick={() => open(link)}
            disabled={opening === link.id}
            data-testid={`staff-tool-launch-${link.id}`}
            className="group flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-emerald-300 hover:shadow-md"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 group-hover:bg-emerald-100">
              {opening === link.id ? <Loader2 size={20} className="animate-spin" /> : <Icon size={20} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 font-semibold text-slate-900">
                <span className="truncate">{link.title}</span>
                <ExternalLink size={13} className="text-slate-400 group-hover:text-emerald-600 shrink-0" />
              </div>
              {link.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{link.description}</p>}
            </div>
          </button>
        );
      })}
    </div>
  );
}
